import { useEffect, useRef, useState } from 'react'
import {
  Badge,
  Group,
  Modal,
  Button,
  Alert,
  Box,
  Divider,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core'
import { SegmentedControl } from '@mantine/core'
import { LineChart } from '@mantine/charts'
import { IconAlertTriangle, IconActivity } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'

import { useCan, useCurrentTenant } from '@/store/auth'
import { causaDeIndisponibilidade, databaseApi, PI_DESABILITADO } from '@/api/database'
import { EmptyState } from '@/components/ui'
import { EstadoDeConsulta } from '@/components/ui/EstadoDeConsulta'

import { SlowQueriesTable } from './performance/SlowQueriesTable'
import { medido } from './performance/ordenacao'

const TIME_OPTIONS = [
  { label: '1h', value: '1' },
  { label: '6h', value: '6' },
  { label: '24h', value: '24' },
  { label: '7d', value: '168' },
]

function truncate(text: string, max: number): string {
  return text.length > max ? text.slice(0, max) + '…' : text
}

export function PerformanceTab() {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  const [selectedDb, setSelectedDb] = useState<string | null>(null)
  const [hours, setHours] = useState('24')
  // O banco vive na URL, como a aba: assim a tela e linkavel e sobrevive ao F5. Mesmo padrao de
  // `DatabasePage` para o parametro `aba`.
  const [params, setParams] = useSearchParams()
  const bancoNaUrl = params.get('banco')

  const bancosQuery = useQuery({
    queryKey: ['rds-pi-bancos', tenantId, selectedDb, hours],
    queryFn: () => databaseApi.getBancosComCarga(tenantId!, selectedDb!, Number(hours)),
    enabled: !!tenantId && !!selectedDb,
    staleTime: 60_000,
  })
  const bancos = bancosQuery.data?.items ?? []
  // Banco que nao esta na lista da janela atual cai em "todos" — sem erro, como `?aba=` invalida.
  const banco = bancoNaUrl && bancos.some((b) => b.nome === bancoNaUrl) ? bancoNaUrl : null

  const escolherBanco = (valor: string | null) => {
    const proxima = new URLSearchParams(params)
    if (valor) proxima.set('banco', valor)
    else proxima.delete('banco')
    setParams(proxima, { replace: true })
  }

  const instancesQuery = useQuery({
    queryKey: ['rds-instances', tenantId],
    queryFn: () => databaseApi.listInstances(tenantId!),
    enabled: !!tenantId,
    staleTime: 60_000,
  })

  // Trocar de instancia limpa o banco: `kong` na instancia A nao e o mesmo da B, e manter a
  // selecao mostraria "sem carga" onde o certo e comecar de novo em "Todos".
  //
  // O `ref` NAO e enfeite: sem ele o efeito roda na MONTAGEM, quando `selectedDb` sai de null para
  // a primeira instancia, e apaga o `?banco=` de quem chegou por link — justamente o caso de uso da
  // URL. Pego por teste (`?banco= na URL e respeitado`), nao por leitura.
  const instanciaAnterior = useRef<string | null>(null)
  useEffect(() => {
    const anterior = instanciaAnterior.current
    instanciaAnterior.current = selectedDb
    if (anterior && selectedDb && anterior !== selectedDb && bancoNaUrl) escolherBanco(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDb])

  useEffect(() => {
    if (instancesQuery.data?.items?.length && !selectedDb) {
      setSelectedDb(instancesQuery.data.items[0].db_identifier)
    }
  }, [instancesQuery.data, selectedDb])

  const slowQueriesQuery = useQuery({
    queryKey: ['rds-slow-queries', tenantId, selectedDb, hours, banco],
    // `horas` viaja COM o dado, e nao lido do seletor na hora do clique: trocar a janela com o
    // painel aberto mandaria o backend procurar a chave numa janela que nao produziu esta linha.
    queryFn: async () => ({
      ...(await databaseApi.getSlowQueries(tenantId!, selectedDb!, 25, Number(hours), banco)),
      horas: Number(hours),
    }),
    enabled: !!tenantId && !!selectedDb,
    staleTime: 60_000,
  })

  const piQuery = useQuery({
    queryKey: ['rds-pi-metrics', tenantId, selectedDb, hours, banco],
    queryFn: () => databaseApi.getPerformanceInsights(tenantId!, selectedDb!, Number(hours), banco),
    enabled: !!tenantId && !!selectedDb,
    staleTime: 30_000,
  })

  const sqData = slowQueriesQuery.data
  const piData = piQuery.data
  // O sinal autoritativo vem da propria instancia, e nao do erro da leitura: a AWS devolve
  // NotAuthorizedException tanto para "PI desligado" quanto para "sem permissao pi:*", e o backend
  // traduz os dois como "nao habilitado". Ver causaDeIndisponibilidade.
  const instancia = instancesQuery.data?.items?.find((i) => i.db_identifier === selectedDb)
  const erroDeLeitura =
    sqData?.error === PI_DESABILITADO || piData?.error === PI_DESABILITADO
      ? PI_DESABILITADO
      : undefined
  const causa = causaDeIndisponibilidade(instancia?.performance_insights_enabled, erroDeLeitura)

  const qc = useQueryClient()
  const can = useCan()
  const podeGerenciar = can('rds', 'manage')
  const [confirmando, setConfirmando] = useState(false)

  const janelaQuery = useQuery({
    queryKey: ['rds-pi-janela', tenantId, selectedDb],
    queryFn: () => databaseApi.getJanelaPi(tenantId!, selectedDb!),
    enabled: !!tenantId && !!selectedDb,
  })
  const janela = janelaQuery.data

  const ativar = useMutation({
    mutationFn: () => databaseApi.ativarPi(tenantId!, selectedDb!),
    onSuccess: () => {
      setConfirmando(false)
      qc.invalidateQueries({ queryKey: ['rds-pi-janela'] })
      qc.invalidateQueries({ queryKey: ['rds-slow-queries'] })
      qc.invalidateQueries({ queryKey: ['rds-pi-metrics'] })
    },
  })

  const desativar = useMutation({
    mutationFn: () => databaseApi.desativarPi(tenantId!, selectedDb!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rds-pi-janela'] }),
  })

  // O texto do botao diz a JANELA, e nao so "Ativar": quem clica precisa saber que isto se
  // desliga sozinho, e quando.
  const ehProducao = (instancia as { ambiente?: string } | undefined)?.ambiente === 'prd'
  const rotuloDaJanela = ehProducao ? '7 dias' : '48 horas'

  /** Data e hora previstas, para o modal nao dizer so "em 48 horas". */
  const janelaPrevista = (): string => {
    const ms = (ehProducao ? 7 * 24 : 48) * 3600 * 1000
    return new Date(Date.now() + ms).toLocaleString('pt-BR')
  }

  // Vale para as TRES causas: em nenhuma delas se pode afirmar que nao existe query lenta.
  const piNotEnabled = causa !== null

  // Erro NO CORPO que nao e o de Performance Insights desabilitado. Antes, qualquer valor de
  // `error` caia num ramo `? null` e a secao inteira desaparecia — sem tabela, sem estado vazio,
  // sem aviso. Desaparecer e pior que mentir: nem da ao leitor o que desconfiar.
  //
  // O de PI desabilitado continua de fora porque ja tem explicacao propria, o alerta amarelo
  // acima, que diz o que fazer.
  const sqFalhouNoCorpo = !!sqData?.error && sqData.error !== PI_DESABILITADO
  const piFalhouNoCorpo = !!piData?.error && piData.error !== PI_DESABILITADO

  const instances = instancesQuery.data?.items ?? []

  if (!tenantId) return null

  return (
    <Stack gap="lg">
      <Group align="flex-end" gap="md">
        <Select
          label="Instância RDS"
          placeholder="Selecione uma instância"
          data={instances.map((i) => ({ value: i.db_identifier, label: i.db_identifier }))}
          value={selectedDb}
          onChange={setSelectedDb}
          disabled={instancesQuery.isLoading}
          maw={400}
          w={400}
        />

        {/* A lista e "bancos COM CARGA na janela", e o rotulo diz isso: medido em 18/08, ampliar
            a janela de 24h para 7 dias NAO revela mais bancos (o PI nao guarda o que nao teve
            carga). Banco ausente esta ocioso ou nao existe na instancia — e isso e informacao,
            nao lacuna da tela. */}
        <Select
          label="Banco"
          description="bancos com carga na janela"
          placeholder="Todos os bancos"
          data={[
            { value: '', label: 'Todos os bancos' },
            ...bancos.map((b) => ({
              value: b.nome,
              // `rdsadmin` e interno da AWS e, medido, e o de MAIOR carga da instancia. Esconder
              // faria os numeros de "todos os bancos" pararem de fechar sem explicacao visivel.
              label: b.interno ? `${b.nome} (interno da AWS)` : b.nome,
            })),
          ]}
          value={banco ?? ''}
          onChange={(v) => escolherBanco(v || null)}
          disabled={!selectedDb || bancosQuery.isLoading}
          w={280}
        />
      </Group>

      {!selectedDb ? (
        <Text c="dimmed" ta="center" py="xl">
          Selecione uma instância para visualizar dados de performance.
        </Text>
      ) : (
      <>
      {/* Slow Queries Section */}
      <Box>
        <Text size="lg" fw={600} mb="sm">
          Slow Queries
        </Text>

        {janela?.ativo && (
          // Informacao, e nao alerta: sem cor de status. A tela nao esta avisando de problema —
          // esta dizendo ate quando o recurso fica ligado.
          <Group justify="space-between" mb="md">
            <Text size="sm" c="dimmed">
              Performance Insights ativo até {formatarQuando(janela.expira_em)}
            </Text>
            <Button
              size="xs"
              variant="default"
              onClick={() => desativar.mutate()}
              loading={desativar.isPending}
            >
              Desligar agora
            </Button>
          </Group>
        )}

        {causa === 'desligado' && !janela?.ativo && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="Performance Insights não habilitado"
            color="yellow"
            mb="md"
          >
            <Stack gap="sm" align="flex-start">
              <Text size="sm">
                Performance Insights não está habilitado para esta instância. Sem ele não há
                queries lentas nem métricas de carga.
              </Text>

              {!podeGerenciar ? (
                // Motivo escrito, e nao botao desabilitado mudo — o defeito que esta sessao
                // consertou no Query Runner.
                <Text size="sm" c="dimmed">
                  Peça a quem tem permissão para gerenciar instâncias RDS, ou habilite o
                  Performance Insights nas configurações do RDS.
                </Text>
              ) : janela && !janela.pode_ativar ? (
                // Nao oferecer o que o servidor vai recusar. E dizer de quem e o problema: nao e
                // do usuario, e falta o servico que desliga a janela no vencimento.
                <Text size="sm" c="dimmed">
                  O desligamento automático não está disponível no momento, então a plataforma não
                  pode ligar o Performance Insights — ele ficaria ligado sem prazo para desligar.
                </Text>
              ) : (
                <Button
                  size="xs"
                  onClick={() => {
                    // Sem o reset, a segunda abertura nasce mostrando a recusa da primeira.
                    ativar.reset()
                    setConfirmando(true)
                  }}
                >
                  Ativar por {rotuloDaJanela}
                </Button>
              )}

              {ativar.isError && (
                <Text size="sm" c="red">
                  {mensagemDeRecusa(ativar.error)}
                </Text>
              )}
            </Stack>
          </Alert>
        )}

        <Modal
          opened={confirmando}
          onClose={() => setConfirmando(false)}
          title="Ativar o Performance Insights"
          centered
        >
          <Stack gap="md">
            <Text size="sm">
              A plataforma vai habilitar o Performance Insights em{' '}
              <strong>{selectedDb}</strong>, com retenção de 7 dias.
            </Text>
            <Text size="sm">
              Ele será <strong>desligado automaticamente em {rotuloDaJanela}</strong>
              {janelaPrevista() ? `, em ${janelaPrevista()}` : ''}. Para desligar antes, use
              “Desligar agora” nesta mesma aba.
            </Text>
            {/* A recusa e resposta ao clique, entao ela aparece onde o clique foi dado.
                Conferido na tela em 18/08/2026: com a mensagem so no <Alert> da pagina, ela era
                pintada ATRAS do overlay e o modal ficava aberto com "Ativar" ainda clicavel —
                quem clicava lia "nao aconteceu nada". O <Alert> mantem o texto tambem, de
                proposito: o modal e a resposta, o alerta e o registro. */}
            {ativar.isError && (
              <Text size="sm" c="red">
                {mensagemDeRecusa(ativar.error)}
              </Text>
            )}
            <Group justify="flex-end">
              <Button variant="default" size="xs" onClick={() => setConfirmando(false)}>
                {ativar.isError ? 'Fechar' : 'Cancelar'}
              </Button>
              {/* Botao que nao pode dar certo sai da tela — nem habilitado, nem desabilitado
                  mudo, que foi o defeito consertado no Query Runner. */}
              {!ativar.isError && (
                <Button size="xs" onClick={() => ativar.mutate()} loading={ativar.isPending}>
                  Ativar
                </Button>
              )}
            </Group>
          </Stack>
        </Modal>

        {causa === 'sem-permissao' && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="Não foi possível ler o Performance Insights"
            color="orange"
            mb="md"
          >
            O Performance Insights está habilitado nesta instância, mas a plataforma não conseguiu
            consultá-lo. O acesso à API de Performance Insights parece não estar liberado para o
            ConsoleAI nesta conta — habilitá-lo novamente no RDS não resolveria.
          </Alert>
        )}

        {causa === 'indeterminado' && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="Não foi possível determinar o estado do Performance Insights"
            color="gray"
            mb="md"
          >
            A consulta falhou e a plataforma não conseguiu ler se o Performance Insights está
            habilitado nesta instância. Isto não significa que não existam queries lentas.
          </Alert>
        )}

        <EstadoDeConsulta
          consulta={slowQueriesQuery}
          falhou={sqFalhouNoCorpo}
          carregando={
            <Stack gap="xs">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} height={36} />
              ))}
            </Stack>
          }
          vazio={!sqData?.items?.length}
          estadoVazio={
            piNotEnabled ? null : banco ? (
              // Filtro de banco sem resultado NAO e "PI desligado": o alerta amarelo mandaria
              // habilitar o PI, que ja esta habilitado. O banco simplesmente nao teve carga.
              <Text c="dimmed" ta="center" py="md" size="sm">
                Nenhuma query com carga registrada no banco <strong>{banco}</strong> nesta janela.
                O Performance Insights está habilitado nesta instância — este banco não teve carga
                no período.
              </Text>
            ) : (
              <Text c="dimmed" ta="center" py="md" size="sm">
                Nenhuma query com carga registrada nesta janela. O Performance Insights está
                habilitado — não houve carga no período. Tente uma janela maior.
              </Text>
            )
          }
        >
          <SlowQueriesTable
            itens={sqData?.items ?? []}
            janelaSegundos={sqData?.janela_segundos}
            bancoEscolhido={banco}
            horas={sqData?.horas ?? Number(hours)}
            tenantId={tenantId!}
            dbIdentifier={selectedDb!}
          />
        </EstadoDeConsulta>
      </Box>

      <Divider />

      {/* DB Load (AAS) Section */}
      <Box>
        <Box mb="sm" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text size="lg" fw={600}>
            DB Load (AAS)
          </Text>
          <SegmentedControl
            size="xs"
            data={TIME_OPTIONS}
            value={hours}
            onChange={(v) => setHours(v)}
          />
        </Box>

        <EstadoDeConsulta
          consulta={piQuery}
          falhou={piFalhouNoCorpo}
          carregando={<Skeleton height={200} />}
          vazio={!piData?.points?.length}
          estadoVazio={
            piNotEnabled ? (
              // Nao repetir o alerta amarelo daqui: ele ja esta acima, e dizer duas vezes na mesma
              // tela ensina o usuario a ignorar aviso. Mas `null` deixava um titulo "DB Load (AAS)"
              // com o seletor de periodo e meia tela em branco embaixo — que le como quebrado ou
              // como carregando eterno. Visto na conferencia de 2026-08-14, e so na foto.
              <Text c="dimmed" size="sm" ta="center" py="md">
                A carga do banco também vem do Performance Insights, desabilitado nesta instância.
              </Text>
            ) : (
              <EmptyState
                bare
                size="sm"
                accent="info"
                icon={IconActivity}
                title="Sem dados de carga"
                description="Performance Insights não retornou amostras para o período selecionado."
              />
            )
          }
        >
          <LineChart
            h={250}
            data={(piData?.points ?? []).map((p) => ({
              ts: new Date(p.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              aas: p.value,
            }))}
            dataKey="ts"
            series={[{ name: 'aas', color: 'indigo.6', label: 'AAS' }]}
            curveType="monotone"
            withDots={false}
            withTooltip
          />
        </EstadoDeConsulta>
      </Box>
      </>
      )}
    </Stack>
  )
}


/** Data e hora, e nao "em 7 dias": prazo relativo obriga o leitor a fazer conta. */
function formatarQuando(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(String(iso).replace(' ', 'T'))
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString('pt-BR')
}

/**
 * Traduz a recusa do servidor. O `motivo` e contrato — a tela escolhe o texto por ele, e nao pela
 * mensagem, que muda sem aviso.
 */
function mensagemDeRecusa(erro: unknown): string {
  const motivo = (erro as { response?: { data?: { detail?: { motivo?: string } } } })
    ?.response?.data?.detail?.motivo
  switch (motivo) {
    case 'varredura_inativa':
      return 'O desligamento automático não está disponível, então a ativação foi recusada — o Performance Insights ficaria ligado sem prazo.'
    case 'gmud_requerida':
      return 'Esta instância é de produção e a mudança exige GMUD aprovada. Abra a GMUD e tente de novo informando o número.'
    case 'gmud_nao_aprovada':
      return 'A GMUD informada não está aprovada para esta mudança.'
    case 'ja_ativo_fora_do_consoleai':
      return 'O Performance Insights já está ligado nesta instância, mas não foi a plataforma que o ligou — por isso ela não vai desligá-lo sozinha.'
    case 'ja_ativo_com_janela':
      // Faltava, e caía na mensagem genérica. Chega numa corrida — duas abas, ou dois cliques
      // antes do primeiro responder — e a tela precisa dizer que já está feito, não que falhou.
      return 'Já existe uma janela ativa para esta instância. Recarregue a página para ver até quando ela vale.'
    case 'instancia_ausente':
      return 'Instância não encontrada nas regiões configuradas.'
    case 'escrita_nao_concedida':
      // A plataforma nao tem `rds:ModifyDBInstance` em conta de cliente, e isso e DECISAO
      // (docs/architecture/performance-insights-leitura-permanente.md), nao lacuna. A mensagem
      // diz o caminho real, porque "nao foi possivel" deixava a pessoa tentando de novo.
      return 'A plataforma não altera configuração de RDS em conta de cliente — ela apenas lê. Ligar o Performance Insights é ação administrativa na conta, ou uma linha no IaC do cliente (performance_insights_enabled).'
    default:
      return 'Não foi possível ativar o Performance Insights.'
  }
}
